// Honest date formatting helpers. Any input that cannot produce a real,
// human-readable date — null / undefined / "" / unparseable / "NaN" — returns
// an em-dash "—" (U+2014). They NEVER surface "Invalid Date", "null", "NaN",
// or a 1970-epoch date conjured from garbage.

const EM_DASH = "—"; // U+2014

const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const DATE_TIME_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

// UTC-pinned date label — for calendar dates (effective/accepted/termination
// dates) that are date-only on the backend and must NOT drift a day in
// negative-offset timezones. Use this, not formatDate, for contract/agreement
// dates so a user west of UTC sees the same day the backend stored.
const DATE_FORMAT_UTC = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

function parse(iso?: string | null): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/** Formats an ISO date as e.g. "Jun 8, 2026", or "—" for unusable input. */
export function formatDate(iso?: string | null): string {
  const date = parse(iso);
  return date ? DATE_FORMAT.format(date) : EM_DASH;
}

/** Formats an ISO datetime as e.g. "Jun 8, 2026, 2:15 PM", or "—" for unusable input. */
export function formatDateTime(iso?: string | null): string {
  const date = parse(iso);
  return date ? DATE_TIME_FORMAT.format(date) : EM_DASH;
}

/**
 * Formats a date-only ISO value in UTC as e.g. "Jun 8, 2026", or "—" for
 * unusable input. Use for backend calendar dates (effective/accepted/
 * termination) so they don't drift a day in negative-offset timezones.
 */
export function formatDateUtc(iso?: string | null): string {
  const date = parse(iso);
  return date ? DATE_FORMAT_UTC.format(date) : EM_DASH;
}
